/**
 * Availability Request Response Modal
 *
 * Modal for responding to availability requests with job details and response options
 */

import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from './Modal';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useToast } from '../contexts/ToastContext';
import { api } from '../lib/api';
import { Clock, MapPin, DollarSign, Briefcase } from 'lucide-react';

interface AvailabilityRequestData {
  availabilityRequestId: string;
  jobDescription: string;
  cityState: string;
  availabilityWindow: string;
  jobLocation: string;
  expiresAt?: string;
  responseStatus?: 'pending' | 'available' | 'unavailable';
  responseMessage?: string;
}

interface AvailabilityRequestResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestData: AvailabilityRequestData | null;
  notificationId?: string; // Optional notification ID to mark as read after responding
}

export const AvailabilityRequestResponseModal = ({
  isOpen,
  onClose,
  requestData,
  notificationId
}: AvailabilityRequestResponseModalProps) => {
  const [responseMessage, setResponseMessage] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [showMessageInput, setShowMessageInput] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const respondMutation = useMutation({
    mutationFn: async ({ status, message }: { status: 'available' | 'unavailable'; message: string }) => {
      if (!requestData?.availabilityRequestId) {
        throw new Error('No availability request ID provided');
      }

      return api.put(
        `/api/workflows/availability-request/${requestData.availabilityRequestId}/respond`,
        {
          responseStatus: status,
          responseMessage: message
        }
      );
    },
    onSuccess: async (data, variables) => {
      const statusText = variables.status === 'available' ? 'Available' : 'Unavailable';
      showSuccess('Response Submitted', `You've marked yourself as ${statusText}`);

      // If this response came from a notification, mark it as read
      if (notificationId) {
        try {
          console.log('[AvailabilityRequestResponseModal] Marking notification as read:', notificationId);
          await api.patch(`/api/workflows/notifications/${notificationId}/read`);
        } catch (error) {
          console.error('[AvailabilityRequestResponseModal] Failed to mark notification as read:', error);
          // Don't block the success flow if marking as read fails
        }
      }

      // Invalidate queries to refresh data across all components
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['availability-requests'] }); // Invalidate all availability request queries
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/dashboard'] }); // Refresh dashboard
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/needs-attention'] }); // Drop it from the attention card now that it's answered

      // Reset form and close modal
      setResponseMessage('');
      setCharCount(0);
      setShowMessageInput(false);
      onClose();
    },
    onError: (error: any) => {
      showError('Failed to Submit Response', error?.response?.data?.error || 'Please try again');
    }
  });

  const handleResponseChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= 100) {
      setResponseMessage(text);
      setCharCount(text.length);
    }
  };

  // Reset form state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setResponseMessage('');
      setCharCount(0);
      setShowMessageInput(false);
    }
  }, [isOpen]);

  const handleAvailable = () => {
    respondMutation.mutate({ status: 'available', message: responseMessage });
  };

  const handleUnavailable = () => {
    respondMutation.mutate({ status: 'unavailable', message: responseMessage });
  };

  if (!requestData) {
    return null;
  }

  const hasAlreadyResponded = requestData.responseStatus && requestData.responseStatus !== 'pending';

  const handleChangeResponse = () => {
    // If changing to available, show message input first
    if (requestData.responseStatus === 'unavailable') {
      setShowMessageInput(true);
      return;
    }

    // If changing from available to unavailable, clear message and submit
    respondMutation.mutate({
      status: 'unavailable',
      message: ''
    });
  };

  const handleSubmitChangeWithMessage = () => {
    respondMutation.mutate({
      status: 'available',
      message: responseMessage
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={hasAlreadyResponded ? "Your Response" : "Availability Request"}
      size="medium"
      showCloseButton={true}
      closeOnOverlayClick={true}
    >
      <div className="space-y-6">
        {/* Job Description */}
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <Briefcase className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">Job Description</h3>
              <p className="text-gray-700 mt-1">{requestData.jobDescription}</p>
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-green-500 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-gray-900">Location</h3>
            <p className="text-gray-700">{requestData.cityState}</p>
          </div>
        </div>

        {/* Availability Window */}
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-amber-500 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-gray-900">Availability Window</h3>
            <p className="text-gray-700">{requestData.availabilityWindow}</p>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 pt-4">
          {hasAlreadyResponded ? (
            showMessageInput ? (
              /* Show message input when changing from unavailable to available */
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                  <svg className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-blue-800">
                    You're changing your response to <strong>Available</strong>. Add a message about your availability (optional).
                  </p>
                </div>

                {/* Response Message Input */}
                <div className="space-y-2">
                  <label htmlFor="response-message" className="block text-sm font-medium text-gray-900">
                    Your Response (Optional)
                    <span className="text-gray-500 font-normal ml-2">
                      {charCount}/100 characters
                    </span>
                  </label>
                  <Textarea
                    id="response-message"
                    value={responseMessage}
                    onChange={handleResponseChange}
                    placeholder="Add a short message about your availability..."
                    className="w-full min-h-[80px]"
                    disabled={respondMutation.isPending}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowMessageInput(false)}
                    disabled={respondMutation.isPending}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitChangeWithMessage}
                    disabled={respondMutation.isPending}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    {respondMutation.isPending ? 'Updating...' : 'Confirm Available'}
                  </Button>
                </div>
              </div>
            ) : (
              /* View for changing existing response */
              <div className="space-y-4">
                <div className={`p-4 rounded-lg border-2 ${
                  requestData.responseStatus === 'available'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-gray-50 border-gray-300'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {requestData.responseStatus === 'available' ? (
                      <>
                        <div className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center">
                          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-lg font-semibold text-green-900">
                          Current Response: Available
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="h-8 w-8 rounded-full bg-gray-400 flex items-center justify-center">
                          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                        <span className="text-lg font-semibold text-gray-900">
                          Current Response: Unavailable
                        </span>
                      </>
                    )}
                  </div>

                  {requestData.responseMessage && (
                    <div className="mt-3 pl-10">
                      <p className="text-sm font-medium text-gray-700 mb-1">Your Previous Message:</p>
                      <p className="text-gray-800 italic">"{requestData.responseMessage}"</p>
                    </div>
                  )}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <svg className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-amber-800">
                    Click the button below to change your response.
                  </p>
                </div>

                {/* Change Response Button */}
                <Button
                  onClick={handleChangeResponse}
                  disabled={respondMutation.isPending}
                  className={`w-full ${
                    requestData.responseStatus === 'available'
                      ? 'bg-gray-600 hover:bg-gray-700'
                      : 'bg-green-600 hover:bg-green-700'
                  } text-white`}
                >
                  {respondMutation.isPending ? (
                    'Updating...'
                  ) : (
                    <>
                      Change to {requestData.responseStatus === 'available' ? 'Unavailable' : 'Available'}
                    </>
                  )}
                </Button>
              </div>
            )
          ) : (
            /* Editable form for new responses */
            <>
              {/* Response Message */}
              <div className="space-y-2">
                <label htmlFor="response-message" className="block text-sm font-medium text-gray-900">
                  Your Response (Optional)
                  <span className="text-gray-500 font-normal ml-2">
                    {charCount}/100 characters
                  </span>
                </label>
                <Textarea
                  id="response-message"
                  value={responseMessage}
                  onChange={handleResponseChange}
                  placeholder="Add a short message about your availability..."
                  className="w-full min-h-[80px]"
                  disabled={respondMutation.isPending}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <Button
                  onClick={handleAvailable}
                  disabled={respondMutation.isPending}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  {respondMutation.isPending ? 'Submitting...' : "Yes, I'm Available"}
                </Button>
                <Button
                  onClick={handleUnavailable}
                  disabled={respondMutation.isPending}
                  variant="outline"
                  className="flex-1"
                >
                  {respondMutation.isPending ? 'Submitting...' : "No, I'm Unavailable"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AvailabilityRequestResponseModal;
